/**
 * CompactMetricReadout — small label/value readouts for dense demo chrome.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../styling/index.js";

type MetricReadoutOrder = "label-value" | "value-label" | "inline";

interface MetricTone {
  readonly text?: string;
  readonly border?: string;
  readonly background?: string;
}

export interface CompactMetricReadoutProps {
  readonly label: string;
  readonly value: React.ReactNode;
  readonly order?: MetricReadoutOrder;
  readonly tone?: MetricTone;
  readonly color?: string;
  readonly valueColor?: string;
  readonly labelColor?: string;
  readonly minWidth?: number | string;
  readonly compact?: boolean;
  readonly monoValue?: boolean;
  readonly className?: string;
  readonly valueClassName?: string;
  readonly labelClassName?: string;
  readonly style?: React.CSSProperties;
  readonly valueStyle?: React.CSSProperties;
  readonly labelStyle?: React.CSSProperties;
  readonly ariaLabel?: string;
}

const DEFAULT_VALUE_COLOR = "var(--csk-metric-value, #e8f0f8)";
const DEFAULT_LABEL_COLOR = "var(--csk-metric-label, #6b7a8d)";

function valueText(value: React.ReactNode): string {
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

export const CompactMetricReadout = React.memo(function CompactMetricReadout({
  label,
  value,
  order = "label-value",
  tone,
  color,
  valueColor,
  labelColor,
  minWidth,
  compact = false,
  monoValue = true,
  className,
  valueClassName,
  labelClassName,
  style,
  valueStyle,
  labelStyle,
  ariaLabel,
}: CompactMetricReadoutProps): React.JSX.Element {
  const resolvedValueColor = valueColor ?? color ?? tone?.text ?? DEFAULT_VALUE_COLOR;
  const resolvedLabelColor = labelColor ?? tone?.text ?? DEFAULT_LABEL_COLOR;
  const resolvedAriaLabel = ariaLabel ?? `${valueText(value)} ${label}`.trim();

  if (order === "inline") {
    return (
      <span
        aria-label={resolvedAriaLabel}
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold leading-none",
          monoValue && "font-mono",
          className,
        )}
        style={{
          border: tone?.border ? `1px solid ${tone.border}` : undefined,
          background: tone?.background,
          color: resolvedValueColor,
          ...style,
        }}
      >
        <span>{label}</span>
        <span>{value}</span>
      </span>
    );
  }

  const valueNode = (
    <div
      aria-label={resolvedAriaLabel}
      className={cn(
        "font-extrabold leading-none",
        compact ? "text-base" : "text-lg",
        monoValue && "font-mono",
        valueClassName,
      )}
      style={{ color: resolvedValueColor, ...valueStyle }}
    >
      {value}
    </div>
  );
  const labelNode = (
    <div
      className={cn(
        "font-bold uppercase tracking-[0.1em]",
        compact ? "text-[8px]" : "text-[10px]",
        labelClassName,
      )}
      style={{ color: resolvedLabelColor, ...labelStyle }}
    >
      {label}
    </div>
  );

  return (
    <div className={cn("text-center", className)} style={{ minWidth, ...style }}>
      {order === "value-label" ? (
        <>
          {valueNode}
          {labelNode}
        </>
      ) : (
        <>
          {labelNode}
          {valueNode}
        </>
      )}
    </div>
  );
});
